import axios from "axios";
import { getCrmApiUrl, getClientApiUrl, getCrmAuthHeaders } from "../config/apiConfig";
import { STORAGE_KEYS, ERROR_MESSAGES } from "../../utils/constants";
import { DEFAULT_OPERATOR } from "../../utils/operators";
import { getAccessToken } from "../constants/authApis";
import {
  setProfile,
  setDevices,
  removeDevice as removeDeviceAction,
} from "./slice/accountSlice";
import store from "./store";

/**
 * Account Business Logic — MangoTV
 * Pattern: mirrors glotv redux/account.js (adapted for MangoTV)
 *
 * API endpoints:
 *   GET  CRM /subscriber/  — fetch profile
 *   PUT  CRM /subscriber/  — update profile / change password
 *   GET  Client /api/client/v1/{op}/users/{id}/devices  — list devices
 *   DELETE Client /api/client/v1/{op}/users/{id}/devices/{did}  — remove device
 */

// ==============================================================================
// HELPERS
// ==============================================================================

const getOperator = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SELECTED_OPERATOR);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return DEFAULT_OPERATOR;
};

const getSubscriberUid = () => {
  return localStorage.getItem(STORAGE_KEYS.USERNAME) || "";
};

// ==============================================================================
// PROFILE
// ==============================================================================

/**
 * Fetch the current user's profile from CRM subscriber endpoint.
 * GET /subscriber/?operator_uid={op}&subscriber_uid={username}&limit=30
 *
 * Stores result in Redux accountSlice.profile
 * @returns {Object|null} — the profile object or null
 */
export const fetchProfile = async () => {
  const operator = getOperator();
  const subscriberUid = getSubscriberUid();
  const accessToken = getAccessToken();

  if (!subscriberUid) {
    console.warn("fetchProfile: no subscriber_uid found");
    return null;
  }

  try {
    const response = await axios.get(
      `${getCrmApiUrl()}/subscriber/?operator_uid=${operator.operator_uid}&subscriber_uid=${subscriberUid}&limit=30`,
      {
        headers: {
          ...getCrmAuthHeaders(),
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      }
    );

    if (response.data.status === "ok" && response.data.data?.length > 0) {
      const profile = response.data.data[0];
      // Persist CRM-issued subscriber_uid for purchase / history / cancel flows.
      // `profile.username` is the mango-prefixed identifier assigned at signup
      // (e.g. "mango10000021366") — distinct from the email-based STORAGE_KEYS.USERNAME.
      if (profile.username) {
        localStorage.setItem("mango_subscriber_uid", profile.username);
      }
      store.dispatch(
        setProfile({
          first_name: profile.first_name || "",
          last_name: profile.last_name || "",
          email: profile.email || "",
          username: profile.username || "",
          date_of_birth: profile.date_of_birth || "",
        })
      );
      return profile;
    }

    return null;
  } catch (error) {
    console.error("fetchProfile error:", error.message);
    return null;
  }
};

/**
 * Update the user's profile (first name, last name, date of birth).
 * PUT /subscriber/?operator_uid={op}&subscriber_uid={username}
 *
 * @param {string} firstName
 * @param {string} lastName
 * @param {string} [dateOfBirth] — optional DOB string
 * @returns {boolean} — success
 */
export const updateProfile = async (firstName, lastName, dateOfBirth) => {
  const operator = getOperator();
  const subscriberUid = getSubscriberUid();
  const accessToken = getAccessToken();

  // Build request body — only include fields that have values
  const body = {};
  if (firstName) body.first_name = firstName;
  if (lastName) body.last_name = lastName;
  if (dateOfBirth) body.date_of_birth = dateOfBirth;

  try {
    const response = await axios.put(
      `${getCrmApiUrl()}/subscriber/?operator_uid=${operator.operator_uid}&subscriber_uid=${subscriberUid}`,
      body,
      {
        headers: {
          ...getCrmAuthHeaders(),
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      }
    );

    if (
      response.data.message === "subscriber account updated" ||
      response.data.status === "ok"
    ) {
      // Update Redux store with new values
      const profileUpdate = { first_name: firstName, last_name: lastName };
      if (dateOfBirth) profileUpdate.date_of_birth = dateOfBirth;
      store.dispatch(setProfile(profileUpdate));
      return true;
    }

    console.warn("[updateProfile] Unexpected response:", response.data);
    return false;
  } catch (error) {
    console.error("updateProfile error:", error.message);
    if (error.response) {
      console.error("updateProfile response data:", error.response.data);
    }
    return false;
  }
};

// ==============================================================================
// CHANGE PASSWORD
// ==============================================================================

/**
 * Change the user's password.
 * PUT /subscriber/?operator_uid={op}&subscriber_uid={username}
 *
 * NOTE: The CRM subscriber endpoint accepts a `password` field in the PUT body.
 * We don't verify the old password server-side (CRM doesn't support it),
 * so the current-password field is for UX confirmation only.
 *
 * @param {string} newPassword
 * @returns {{ success: boolean, error: string|null }}
 */
export const changePassword = async (newPassword) => {
  const operator = getOperator();
  const subscriberUid = getSubscriberUid();
  const accessToken = getAccessToken();

  try {
    const response = await axios.put(
      `${getCrmApiUrl()}/subscriber/?operator_uid=${operator.operator_uid}&subscriber_uid=${subscriberUid}`,
      { password: newPassword },
      {
        headers: {
          ...getCrmAuthHeaders(),
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      }
    );

    if (
      response.data.message === "subscriber account updated" ||
      response.data.status === "ok"
    ) {
      return { success: true, error: null };
    }

    return {
      success: false,
      error: response.data.message || ERROR_MESSAGES.GENERIC_ERROR,
    };
  } catch (error) {
    console.error("changePassword error:", error.message);
    return { success: false, error: ERROR_MESSAGES.GENERIC_ERROR };
  }
};

// ==============================================================================
// DEVICES
// ==============================================================================

/**
 * Fetch user's registered devices from the Client API.
 * GET /api/client/v1/{operator_uid}/users/{user_id}/devices
 *
 * @returns {Array|null}
 */
export const fetchUserDevices = async () => {
  const operator = getOperator();
  const accessToken = getAccessToken();
  const userId = localStorage.getItem("mango_user_id");

  if (!userId || !accessToken) {
    console.warn("fetchUserDevices: missing user_id or access_token");
    return null;
  }

  try {
    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operator.operator_uid}/users/${userId}/devices`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (response.data.status === "ok") {
      const devices = response.data.data || [];
      store.dispatch(setDevices(devices));
      return devices;
    }

    return null;
  } catch (error) {
    console.error("fetchUserDevices error:", error.message);
    return null;
  }
};

/**
 * Delete a user device from the Client API.
 * DELETE /api/client/v1/{operator_uid}/users/{user_id}/devices/{device_id}
 *
 * @param {number|string} deviceId
 * @returns {boolean}
 */
export const deleteUserDevice = async (deviceId) => {
  const operator = getOperator();
  const accessToken = getAccessToken();
  const userId = localStorage.getItem("mango_user_id");

  if (!userId || !accessToken) {
    return false;
  }

  try {
    const response = await axios.delete(
      `${getClientApiUrl()}/api/client/v1/${operator.operator_uid}/users/${userId}/devices/${deviceId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    if (response.data.status === "ok") {
      store.dispatch(removeDeviceAction(deviceId));
      return true;
    }

    return false;
  } catch (error) {
    console.error("deleteUserDevice error:", error.message);
    return false;
  }
};
